import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Search, ChevronDown, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/components/ui/select';
import { RansomwareStats } from '@/components/ransomware/RansomwareStats';
import { RansomwareSearchResults } from '@/components/ransomware/RansomwareSearchResults';
import { RecentAttacks } from '@/components/ransomware/RecentAttacks';
import type { SearchType } from '@/lib/types/ransomware';

// Increase debounce delay to reduce API load
const DEBOUNCE_DELAY = 500;

export function RansomwareIntelligence() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Initialize state from URL parameters
  const [searchQuery, setSearchQuery] = useState(searchParams.get('query') || '');
  const [debouncedQuery, setDebouncedQuery] = useState(searchQuery);
  const [searchType, setSearchType] = useState<SearchType>((searchParams.get('type') as SearchType) || 'victim');

  // Debounce search query with increased delay
  useEffect(() => {
    // Don't set up debounce for empty queries
    if (!searchQuery.trim()) {
      setDebouncedQuery('');
      return;
    }

    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, DEBOUNCE_DELAY);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Update URL when debounced search parameters change
  useEffect(() => {
    if (debouncedQuery) {
      setSearchParams({ query: debouncedQuery, type: searchType });
    } else {
      // Clear URL parameters when search is empty
      setSearchParams({});
    }
  }, [debouncedQuery, searchType, setSearchParams]);

  // Handle input changes
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    if (!value) {
      // Clear URL parameters when search is cleared
      navigate('/ransomware', { replace: true });
    }
  };

  // Handle search clear
  const handleClearSearch = () => {
    setSearchQuery('');
    navigate('/ransomware', { replace: true });
  };

  // Handle search type changes
  const handleTypeChange = (value: SearchType) => {
    setSearchType(value);
    if (searchQuery) {
      setSearchParams({ query: searchQuery, type: value });
    }
  };

  return (
    <div className="p-6 space-y-6">
      <h1 className="text-3xl font-ibm-condensed">Attack Monitor</h1>

      <RansomwareStats />

      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder={`Search ${searchType === 'victim' ? 'victim' : 'adversary group'}...`}
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="flex rounded-md border border-input px-3 py-1 shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 h-12 pl-12 text-base bg-background placeholder:text-muted-foreground w-full"
          />
          {searchQuery && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 hover:bg-transparent"
              onClick={handleClearSearch}
            >
              <X className="h-4 w-4 text-muted-foreground hover:text-foreground" />
            </Button>
          )}
        </div>
        <Select value={searchType} onValueChange={handleTypeChange}>
          <SelectTrigger className="w-[180px] h-12">
            <div className="flex items-center gap-2">
              <span className="text-sm">
                {searchType === 'victim' ? 'Victim' : 'Adversary Group'}
              </span>
              <ChevronDown className="h-4 w-4" />
            </div>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="victim">Victim</SelectItem>
            <SelectItem value="group">Adversary Group</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {debouncedQuery ? (
        <RansomwareSearchResults query={debouncedQuery} type={searchType} />
      ) : (
        <RecentAttacks />
      )}
    </div>
  );
}